import React, { useEffect, useMemo, useState } from 'react';
import { useHOA } from '../../contexts/HOAContext';
import { Card } from '../ui';
import {
  analyzePropertyBalancesCsv,
  applyBalanceImport,
  formatCurrency,
  getLatestBalanceUpload,
  getPaymentPortalUrl,
  updatePaymentPortalUrl,
} from '../../services/balanceService';

const todayIso = () => new Date().toISOString().slice(0, 10);

const formatUploadDate = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const CountTile = ({ label, value, tone = 'neutral' }) => {
  const toneClass = tone === 'good'
    ? 'text-emerald-700'
    : tone === 'warn'
      ? 'text-secondary'
      : tone === 'bad'
        ? 'text-red-700'
        : 'text-primary-dark';
  return (
    <div className="p-4 bg-section-bg rounded-2xl text-center">
      <p className={`text-2xl font-extrabold ${toneClass}`}>{value}</p>
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</p>
    </div>
  );
};

const MatchBadge = ({ row }) => {
  if (row.matchOrigin === 'memory') {
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold uppercase tracking-widest">
        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>check_circle</span>
        Previously confirmed
      </span>
    );
  }
  if (row.matchOrigin === 'deterministic') {
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold uppercase tracking-widest">
        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>check_circle</span>
        Matched by {row.matchType}
      </span>
    );
  }
  return null;
};

const PropertySelect = ({ properties, value, onChange, placeholder = 'Choose a property...' }) => (
  <select
    value={value || ''}
    onChange={(event) => onChange(event.target.value || null)}
    className="w-full max-w-sm px-4 py-2.5 bg-white border-2 border-amber-300/80 rounded-2xl text-sm font-medium text-primary-dark outline-none focus:border-secondary focus:ring-4 focus:ring-secondary/15 transition-all"
  >
    <option value="">{placeholder}</option>
    {properties.map((property) => (
      <option key={property.id} value={property.id}>
        {property.situs_address || 'No address'} — {property.owner_name_legal || 'Unknown owner'}
      </option>
    ))}
  </select>
);

const BalanceImportPanel = () => {
  const { hoa } = useHOA();
  const [selectedFile, setSelectedFile] = useState(null);
  const [asOfDate, setAsOfDate] = useState(todayIso());
  const [reviewing, setReviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [paymentUrl, setPaymentUrl] = useState('');
  const [savingUrl, setSavingUrl] = useState(false);
  const [urlSaved, setUrlSaved] = useState(false);
  const [lastUpload, setLastUpload] = useState(null);

  useEffect(() => {
    const initialUrl = hoa?.payment_portal_url || hoa?.settings?.payment_portal_url || '';
    setPaymentUrl(initialUrl);
  }, [hoa?.payment_portal_url, hoa?.settings?.payment_portal_url]);

  useEffect(() => {
    let mounted = true;
    const loadPanelData = async () => {
      if (!hoa?.id) return;
      try {
        const url = await getPaymentPortalUrl(hoa.id);
        if (mounted && url) setPaymentUrl(url);
      } catch (err) {
        console.warn('Payment URL prefill failed:', err?.message || err);
      }
      const upload = await getLatestBalanceUpload(hoa.id);
      if (mounted) setLastUpload(upload);
    };
    loadPanelData();
    return () => {
      mounted = false;
    };
  }, [hoa?.id]);

  const resetReview = () => {
    setPreview(null);
    setDecisions({});
    setResult(null);
    setError('');
  };

  const handleReview = async () => {
    if (!selectedFile || reviewing) return;
    setReviewing(true);
    setError('');
    setResult(null);
    setDecisions({});
    try {
      const summary = await analyzePropertyBalancesCsv({ hoaId: hoa?.id, file: selectedFile, asOfDate });
      setPreview(summary);
    } catch (err) {
      console.error('Balance CSV review failed:', err);
      setError(err?.message || 'Balance CSV could not be reviewed.');
    } finally {
      setReviewing(false);
    }
  };

  const setDecision = (rowNumber, decision) => {
    setDecisions((current) => ({ ...current, [rowNumber]: decision }));
  };

  const confirmAllAi = () => {
    setDecisions((current) => {
      const next = { ...current };
      (preview?.aiRows || []).forEach((row) => {
        if (!next[row.rowNumber]) {
          next[row.rowNumber] = { action: 'confirm' };
        }
      });
      return next;
    });
  };

  const applyRows = useMemo(() => {
    if (!preview) return [];
    const rows = [...(preview.readyRows || [])];

    (preview.aiRows || []).forEach((row) => {
      const decision = decisions[row.rowNumber];
      if (!decision || decision.action === 'skip') return;
      if (decision.action === 'confirm') {
        rows.push(row);
        return;
      }
      if (decision.action === 'override' && decision.propertyId) {
        const property = (preview.properties || []).find((p) => p.id === decision.propertyId);
        rows.push({
          ...row,
          propertyId: decision.propertyId,
          propertyAddress: property?.situs_address,
          ownerName: property?.owner_name_legal,
          matchOrigin: 'manual',
          matchType: 'manual override',
          confidence: null,
        });
      }
    });

    (preview.attentionRows || []).forEach((row) => {
      const decision = decisions[row.rowNumber];
      if (!decision || decision.action !== 'assign' || !decision.propertyId) return;
      const property = (preview.properties || []).find((p) => p.id === decision.propertyId);
      rows.push({
        ...row,
        propertyId: decision.propertyId,
        propertyAddress: property?.situs_address,
        ownerName: property?.owner_name_legal,
        matchOrigin: 'manual',
        matchType: 'manual assignment',
        confidence: null,
      });
    });

    return rows;
  }, [preview, decisions]);

  const pendingAiCount = useMemo(() => (
    (preview?.aiRows || []).filter((row) => {
      const decision = decisions[row.rowNumber];
      return !decision || (decision.action === 'override' && !decision.propertyId);
    }).length
  ), [preview, decisions]);

  const skippedCount = useMemo(() => {
    if (!preview) return 0;
    const aiSkipped = (preview.aiRows || []).filter((row) => decisions[row.rowNumber]?.action === 'skip').length;
    const attentionUnassigned = (preview.attentionRows || []).filter((row) => {
      const decision = decisions[row.rowNumber];
      return !decision || decision.action !== 'assign' || !decision.propertyId;
    }).length;
    return aiSkipped + attentionUnassigned;
  }, [preview, decisions]);

  const handleImport = async () => {
    if (!preview || importing || applyRows.length === 0) return;
    setImporting(true);
    setError('');
    try {
      const summary = await applyBalanceImport({
        hoaId: hoa?.id,
        fileName: preview.fileName,
        asOfDate: preview.asOfDate,
        rows: applyRows,
        totalRows: preview.totalRows,
        skippedRows: skippedCount,
      });
      setResult(summary);
      setPreview(null);
      setDecisions({});
      const upload = await getLatestBalanceUpload(hoa?.id);
      setLastUpload(upload);
    } catch (err) {
      console.error('Balance import failed:', err);
      setError(err?.message || 'Balances could not be imported.');
    } finally {
      setImporting(false);
    }
  };

  const handleSavePaymentUrl = async () => {
    if (savingUrl) return;
    setSavingUrl(true);
    setUrlSaved(false);
    setError('');
    try {
      const savedUrl = await updatePaymentPortalUrl({ hoaId: hoa?.id, paymentPortalUrl: paymentUrl });
      setPaymentUrl(savedUrl || '');
      setUrlSaved(true);
      setTimeout(() => setUrlSaved(false), 3500);
    } catch (err) {
      console.error('Payment portal URL save failed:', err);
      setError(err?.message || 'Payment portal link could not be saved.');
    } finally {
      setSavingUrl(false);
    }
  };

  return (
    <Card>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-2xl bg-section-bg flex items-center justify-center">
          <span className="material-symbols-outlined text-2xl text-secondary">payments</span>
        </div>
        <h3 className="font-bold text-primary-dark text-lg">Payment Management</h3>
      </div>

      <section className="bg-section-bg rounded-[2rem] p-6 space-y-5 mb-6">
        <div>
          <h4 className="text-xl font-bold text-primary-dark mb-1">Payment Portal</h4>
          <p className="text-sm font-medium text-slate-500">Zego or PayLease link shown on homeowner dashboards.</p>
        </div>
        <div className="bg-white rounded-2xl p-5 warm-tactile-shadow border border-amber-100/30 space-y-4">
          <input
            type="url"
            value={paymentUrl}
            onChange={(event) => {
              setPaymentUrl(event.target.value);
              setUrlSaved(false);
            }}
            placeholder="https://..."
            className="w-full px-4 py-3 bg-white border-2 border-amber-300/80 rounded-2xl text-sm text-primary-dark outline-none focus:border-secondary focus:ring-4 focus:ring-secondary/15 transition-all"
          />
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleSavePaymentUrl}
              disabled={!hoa?.id || savingUrl}
              className="bg-secondary text-white px-7 py-3 rounded-full font-bold text-sm hover:brightness-110 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {savingUrl ? 'Saving...' : 'Save Payment Link'}
            </button>
            {paymentUrl && (
              <a href={paymentUrl} target="_blank" rel="noreferrer" className="font-bold text-secondary hover:text-primary-dark transition-all">
                Open link
              </a>
            )}
            {urlSaved && <span className="text-sm font-bold text-emerald-700">Saved.</span>}
          </div>
        </div>
      </section>

      <section className="bg-section-bg rounded-[2rem] p-6 space-y-5">
        <div>
          <h4 className="text-xl font-bold text-primary-dark mb-1">Balance Upload</h4>
          <p className="text-sm font-medium text-slate-500">
            Upload a QuickBooks A/R Aging Summary CSV (or any balance CSV). Rows match automatically by
            assessment code, address, or previously confirmed owner names; the AI matcher proposes the rest for your review.
          </p>
          {lastUpload && (
            <p className="mt-2 text-xs font-bold uppercase tracking-widest text-slate-500">
              Last upload: {lastUpload.filename || 'CSV'} · {formatUploadDate(lastUpload.uploaded_at)} · {lastUpload.imported_rows} balances
            </p>
          )}
        </div>

        <div className="bg-white rounded-2xl p-5 warm-tactile-shadow border border-amber-100/30 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-4">
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => {
                setSelectedFile(event.target.files?.[0] || null);
                resetReview();
              }}
              className="block w-full text-sm font-medium text-primary-dark file:mr-4 file:rounded-full file:border-0 file:bg-secondary file:px-5 file:py-2.5 file:text-sm file:font-bold file:text-white hover:file:brightness-110"
            />
            <input
              type="date"
              value={asOfDate}
              onChange={(event) => {
                setAsOfDate(event.target.value);
                resetReview();
              }}
              className="w-full px-4 py-3 bg-white border-2 border-amber-300/80 rounded-2xl text-sm text-primary-dark outline-none focus:border-secondary focus:ring-4 focus:ring-secondary/15 transition-all"
            />
          </div>

          <button
            type="button"
            onClick={handleReview}
            disabled={!selectedFile || reviewing || !hoa?.id}
            className="bg-secondary text-white px-7 py-3 rounded-full font-bold text-sm hover:brightness-110 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {reviewing ? 'Reviewing & matching names...' : 'Review CSV'}
          </button>
        </div>

        {error && (
          <div className="bg-white rounded-2xl p-4 border border-red-100 text-red-700 text-sm font-bold">
            {error}
          </div>
        )}

        {preview && (
          <div className="bg-white rounded-2xl p-5 warm-tactile-shadow border border-amber-100/30 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <CountTile label="Auto-matched" value={preview.readyRows.length} tone="good" />
              <CountTile label="AI proposed" value={preview.aiRows.length} tone="warn" />
              <CountTile label="Needs review" value={preview.attentionRows.length} tone="bad" />
              <CountTile label="Invalid" value={preview.invalidRows.length} />
              <CountTile label="Rows" value={preview.totalRows} />
            </div>

            {preview.readyRows.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Auto-matched — no action needed</p>
                <div className="overflow-x-auto bg-white rounded-2xl border border-emerald-100">
                  <table className="min-w-full text-left text-sm">
                    <thead className="bg-section-bg text-xs font-bold uppercase tracking-widest text-slate-500">
                      <tr>
                        <th className="px-4 py-3">Statement Name</th>
                        <th className="px-4 py-3">Property</th>
                        <th className="px-4 py-3">Balance</th>
                        <th className="px-4 py-3">Match</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-emerald-100/60">
                      {preview.readyRows.map((row) => (
                        <tr key={`ready-${row.rowNumber}`} data-testid="ready-row">
                          <td className="px-4 py-3 font-bold text-primary-dark">{row.sourceOwnerName || row.sourceAddress || row.sourceAssessmentCode}</td>
                          <td className="px-4 py-3 text-slate-600">
                            <div className="font-bold text-primary-dark">{row.propertyAddress || '—'}</div>
                            <div className="text-xs text-slate-500">{row.ownerName || ''}</div>
                          </td>
                          <td className="px-4 py-3 font-bold text-primary-dark">{formatCurrency(row.balance || 0)}</td>
                          <td className="px-4 py-3"><MatchBadge row={row} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {preview.aiRows.length > 0 && (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-500">AI proposed matches — confirm or override</p>
                  {pendingAiCount > 0 && (
                    <button
                      type="button"
                      onClick={confirmAllAi}
                      className="border border-secondary text-secondary px-5 py-2 rounded-full font-bold text-xs hover:bg-secondary hover:text-white transition-all"
                    >
                      Confirm All ({pendingAiCount})
                    </button>
                  )}
                </div>
                <div className="space-y-3">
                  {preview.aiRows.map((row) => {
                    const decision = decisions[row.rowNumber];
                    return (
                      <div key={`ai-${row.rowNumber}`} data-testid="ai-row" className="p-4 rounded-2xl border border-amber-200 bg-amber-50/40 space-y-3">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="font-bold text-primary-dark">{row.sourceOwnerName}</p>
                            <p className="text-sm font-medium text-slate-600">
                              <span className="material-symbols-outlined align-middle text-secondary" style={{ fontSize: '16px' }}>arrow_forward</span>
                              {' '}{row.propertyAddress || '—'} · {row.ownerName || ''}
                            </p>
                            {row.reasoning && (
                              <p className="text-xs font-medium text-slate-500 mt-1">{row.reasoning}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-extrabold text-primary-dark">{formatCurrency(row.balance || 0)}</p>
                            <p className="text-[10px] font-bold uppercase tracking-widest text-secondary">
                              {Math.round((row.confidence || 0) * 100)}% confidence
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setDecision(row.rowNumber, { action: 'confirm' })}
                            className={`px-5 py-2 rounded-full font-bold text-xs transition-all ${decision?.action === 'confirm'
                              ? 'bg-emerald-600 text-white'
                              : 'border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white'}`}
                          >
                            {decision?.action === 'confirm' ? 'Confirmed' : 'Confirm'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setDecision(row.rowNumber, { action: 'override', propertyId: null })}
                            className={`px-5 py-2 rounded-full font-bold text-xs transition-all ${decision?.action === 'override'
                              ? 'bg-secondary text-white'
                              : 'border border-secondary text-secondary hover:bg-secondary hover:text-white'}`}
                          >
                            Override
                          </button>
                          <button
                            type="button"
                            onClick={() => setDecision(row.rowNumber, { action: 'skip' })}
                            className={`px-5 py-2 rounded-full font-bold text-xs transition-all ${decision?.action === 'skip'
                              ? 'bg-slate-600 text-white'
                              : 'border border-slate-400 text-slate-600 hover:bg-slate-600 hover:text-white'}`}
                          >
                            {decision?.action === 'skip' ? 'Skipped' : 'Skip'}
                          </button>
                          {decision?.action === 'override' && (
                            <PropertySelect
                              properties={preview.properties || []}
                              value={decision.propertyId}
                              onChange={(propertyId) => setDecision(row.rowNumber, { action: 'override', propertyId })}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {preview.attentionRows.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Needs review — assign a property or leave to skip</p>
                <div className="space-y-3">
                  {preview.attentionRows.map((row) => {
                    const decision = decisions[row.rowNumber];
                    return (
                      <div key={`attention-${row.rowNumber}`} data-testid="attention-row" className="p-4 rounded-2xl border border-red-200 bg-red-50/30 space-y-3">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="font-bold text-primary-dark">
                              {row.sourceOwnerName || row.sourceAddress || row.sourceAssessmentCode || `Row ${row.rowNumber}`}
                            </p>
                            <p className="text-xs font-medium text-red-700">{row.reason}</p>
                          </div>
                          <p className="text-lg font-extrabold text-primary-dark">{formatCurrency(row.balance || 0)}</p>
                        </div>
                        <PropertySelect
                          properties={preview.properties || []}
                          value={decision?.propertyId}
                          onChange={(propertyId) => setDecision(row.rowNumber, propertyId ? { action: 'assign', propertyId } : null)}
                          placeholder="Skip this row (no matching property)"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {preview.invalidRows.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Invalid rows — not importable</p>
                <div className="bg-white rounded-2xl p-4 border border-amber-100/30 text-sm font-medium text-slate-500 space-y-1">
                  {preview.invalidRows.map((row) => (
                    <p key={`invalid-${row.rowNumber}`}>
                      Row {row.rowNumber}: {row.sourceOwnerName || 'No name'} — {row.reason}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-amber-100/50">
              <button
                type="button"
                data-testid="apply-balances"
                onClick={handleImport}
                disabled={applyRows.length === 0 || importing || pendingAiCount > 0}
                className="bg-primary-dark text-white px-7 py-3 rounded-full font-bold text-sm hover:brightness-110 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importing ? 'Applying...' : `Apply ${applyRows.length} Balance${applyRows.length === 1 ? '' : 's'}`}
              </button>
              {pendingAiCount > 0 && (
                <span className="text-sm font-bold text-secondary">
                  {pendingAiCount} AI match{pendingAiCount === 1 ? '' : 'es'} still need{pendingAiCount === 1 ? 's' : ''} a decision.
                </span>
              )}
              {skippedCount > 0 && pendingAiCount === 0 && (
                <span className="text-sm font-medium text-slate-500">
                  {skippedCount} row{skippedCount === 1 ? '' : 's'} will be skipped.
                </span>
              )}
              <button
                type="button"
                onClick={resetReview}
                className="border border-secondary text-secondary px-7 py-3 rounded-full font-bold text-sm hover:bg-secondary hover:text-white transition-all"
              >
                Clear Review
              </button>
            </div>
          </div>
        )}

        {result && (
          <div data-testid="import-result" className="bg-white rounded-2xl p-5 warm-tactile-shadow border border-emerald-100 text-emerald-800 text-sm font-bold">
            {result.imported} balance{result.imported === 1 ? '' : 's'} updated
            {result.newMappingsSaved > 0 ? ` · ${result.newMappingsSaved} new name mapping${result.newMappingsSaved === 1 ? '' : 's'} saved to memory` : ''}
            {result.skipped > 0 ? ` · ${result.skipped} row${result.skipped === 1 ? '' : 's'} skipped` : ''}.
            Homeowner dashboards now show the updated amounts.
          </div>
        )}
      </section>
    </Card>
  );
};

export default BalanceImportPanel;
